import type { LoanApplicationData } from "../../hooks/useNbiForm";
import { normalize, stripNonDigits, titleCase } from "./common";
import type { EmploymentContract, SetFieldValueCallback } from "./models";
import { extractReferences } from "./parserUtils";

const syncAbnToSelfEmployedIncomes = (
    formData: LoanApplicationData,
    applicantId: string,
    businessAbn: string,
    references: Map<string, any>,
    setFieldValue: SetFieldValueCallback
) => {
    const incomes = Array.isArray(formData.incomes) ? formData.incomes : [];
    let updated = false;
    const updatedIncomes = incomes.map((income: any) => {
        if (income.applicantId === applicantId && income.incomeType === "Self-employed" && !income.businessAbn) {
            updated = true;
            const updatedRefs = new Map(income.references || new Map());
            updatedRefs.set("businessAbn", references.get("businessAbn"));
            return { ...income, businessAbn, references: updatedRefs };
        }
        return income;
    });
    if (updated) setFieldValue("incomes" as any, updatedIncomes);
};

const getEmploymentIndex = (
    applicantId: string,
    employmentId: string,
    employerName: string,
    currentFormData: Array<any>
): number => {
    const normalizedEmployerName = normalize(employerName);
    return currentFormData.findIndex(
        (e) =>
            e.applicantId === applicantId &&
            (e.id === employmentId || normalize(e.employerName) === normalizedEmployerName)
    );
};

const handleEmploymentContract = (
    data: EmploymentContract,
    currentFormData: LoanApplicationData,
    setFieldValue: SetFieldValueCallback,
    applicantId: string | null
): string | null => {
    const safeApplicantId = applicantId != null ? String(applicantId) : null;
    if (safeApplicantId === null) {
        console.log(`Skip to process emploment contract without associated applicant.`);
        return null;
    }

    // Multi-applicant: upsert employments[] and track badges per applicant
    const existingEmployments = Array.isArray(currentFormData.employments) ? currentFormData.employments : [];
    const employerName = titleCase(data.companyName || "");
    let employmentId = data.id.toString();
    const existingIndex = getEmploymentIndex(safeApplicantId, employmentId, employerName, existingEmployments);
    if (existingIndex >= 0) {
        employmentId = existingEmployments[existingIndex].id;
    }

    if (existingIndex >= 0 && data.isIncomplete) {
        console.log("employment exists and we just need the id");
        return employmentId;
    }

    const businessAbn = stripNonDigits(data.businessAbn);

    const currentEmploymentBusinessAbn = existingIndex >= 0 ? existingEmployments[existingIndex].businessAbn : "";

    // override employment type if it's contractor
    let employmentType: string | undefined;

    if (data.isContractor === true) {
        employmentType = "Contractor";
    } else if (existingIndex >= 0) {
        employmentType = (existingEmployments[existingIndex] as any).employmentType;
    }

    const references = extractReferences(data, existingEmployments[existingIndex]?.references);

    const nextEmployment = {
        id: employmentId,
        applicantId: safeApplicantId,
        employerName: employerName,
        businessAbn: businessAbn === "" ? currentEmploymentBusinessAbn : businessAbn,
        jobTitle: data.jobTitle || "",
        employmentType: employmentType,
        employmentStartDate: data.startDate || "",
        employmentEndDate: data.endDate || "",
        references: references,
    };

    const updatedEmployments = [...existingEmployments];
    if (existingIndex >= 0) {
        updatedEmployments[existingIndex] = nextEmployment as (typeof updatedEmployments)[number];
    } else {
        updatedEmployments.push(nextEmployment as (typeof updatedEmployments)[number]);
    }

    setFieldValue("employments" as any, updatedEmployments);

    if (data.isContractor && businessAbn) {
        syncAbnToSelfEmployedIncomes(currentFormData, safeApplicantId, businessAbn, references, setFieldValue);
    }

    // Handle monthly gross salary (now comes as a float from backend)
    if (data.monthlyGrossSalary) {
        const monthlySalary = data.monthlyGrossSalary;
        if (typeof monthlySalary === "number" && !Number.isNaN(monthlySalary)) {
            // TODO: Create a PAYG income entry with this salary information
            // For now, we'll just log it since there's no direct annualSalary field
            console.log("Employment contract salary info:", {
                monthly: monthlySalary,
                annual: monthlySalary * 12,
            });
        }
    }

    return employmentId;
};

export { handleEmploymentContract };
